import React, { useEffect, useRef } from "react";
import Icon from "../../../components/AppIcon";
import ActionButton from "./ActionButton";

const ConfirmationModal = ({
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  variant = "warning",
  isOpen = true,
}) => {
  const modalRef = useRef(null);
  
  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onCancel();
      }
    };
    
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onCancel]);
  
  // Focus trap
  useEffect(() => {
    if (isOpen) {
      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabIndex="-1"])'
      );
      
      if (focusableElements && focusableElements.length > 0) {
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        
        const handleTabKey = (e) => {
          if (e.key === "Tab") {
            if (e.shiftKey && document.activeElement === firstElement) {
              e.preventDefault();
              lastElement.focus();
            } else if (!e.shiftKey && document.activeElement === lastElement) {
              e.preventDefault();
              firstElement.focus();
            }
          }
        };
        
        firstElement.focus();
        modalRef.current.addEventListener("keydown", handleTabKey);
        
        return () => {
          modalRef.current?.removeEventListener("keydown", handleTabKey);
        };
      }
    }
  }, [isOpen]);
  
  // Variant configurations
  const variantConfig = {
    warning: {
      icon: "AlertTriangle",
      iconColor: "text-warning",
      confirmVariant: "danger",
    },
    danger: {
      icon: "AlertOctagon",
      iconColor: "text-error",
      confirmVariant: "danger",
    },
    info: {
      icon: "Info",
      iconColor: "text-info",
      confirmVariant: "primary",
    },
  };
  
  const config = variantConfig[variant] || variantConfig.warning;
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby={title} role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" aria-hidden="true" onClick={onCancel}></div>
      
      {/* Modal */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div 
          ref={modalRef}
          className="relative bg-white rounded-lg max-w-md w-full shadow-xl transform transition-all"
          role="alertdialog"
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          {/* Close button */}
          <button
            type="button"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md"
            onClick={onCancel}
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
          
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center mb-4">
              <div className={`mr-3 flex-shrink-0 ${config.iconColor}`}>
                <Icon name={config.icon} size={24} />
              </div>
              <h3 className="text-lg font-medium text-gray-900" id="modal-title">
                {title}
              </h3>
            </div>
            
            {/* Content */}
            <div className="mt-2">
              <p className="text-sm text-gray-500" id="modal-description">
                {message}
              </p>
            </div>
            
            {/* Actions */}
            <div className="mt-6 flex justify-end space-x-3">
              <ActionButton
                variant="secondary"
                onClick={onCancel}
              >
                {cancelText}
              </ActionButton>
              <ActionButton
                variant={config.confirmVariant}
                onClick={onConfirm}
              >
                {confirmText}
              </ActionButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;